import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, Camera, DownloadSimple, FileText, Lock } from "@phosphor-icons/react";
import api, { formatApiError, API_BASE } from "../../lib/api";

/**
 * Documentation panel for the Owner Control Room.
 * — Only the superuser sees ALL three docs.
 * — `technical` and `integration` are owner-only (Lock icon).
 * — `user-guide` gets published to every authenticated user once regenerated.
 * — REGENERATE ALL button re-runs the builder in-process.
 * — REFRESH SHOTS button spawns the Playwright capture script (~40s) so the
 *   next regenerate embeds fresh figures. Kept separate because it needs
 *   Chromium and is slower than pure docx assembly.
 */
export default function DocumentationSection() {
  const [items, setItems] = useState([]);
  const [shotsCount, setShotsCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [shotsBusy, setShotsBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/platform/docs/status");
      setItems(data.items || []);
      setShotsCount(data.screenshots_count || 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load docs status");
    }
  };
  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    if (!window.confirm("Regenerate all three documentation files?\n\n• Technical Architecture (owner-only)\n• Integration Guide (owner-only)\n• Complete User Guide — will be re-published to every authenticated user.")) return;
    setBusy(true);
    try {
      await api.post("/platform/docs/regenerate");
      toast.success("Documentation regenerated · User Guide is now published");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Regenerate failed");
    } finally {
      setBusy(false);
    }
  };

  const refreshShots = async () => {
    if (!window.confirm(
      "Refresh all embedded screenshots?\n\n" +
      "This spins up headless Chromium, signs in as the seeded demo/owner accounts, " +
      "and captures ~11 PNGs of the live preview UI. Usually takes 40-60 seconds.\n\n" +
      "After it finishes, click REGENERATE ALL to embed the new shots in the .docx files."
    )) return;
    setShotsBusy(true);
    try {
      const { data } = await api.post("/platform/docs/refresh-screenshots");
      toast.success(`Captured ${data.count} screenshots — click REGENERATE ALL to embed them.`, { duration: 8000 });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Screenshot capture failed");
    } finally {
      setShotsBusy(false);
    }
  };

  const dlUrl = (kind) => `${API_BASE}/api/platform/docs/download/${kind}`;

  return (
    <section className="border border-zinc-950 mt-6" data-testid="documentation-section">
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 flex items-center justify-between">
        <span className="font-mono text-[11px] tracking-[0.2em] uppercase flex items-center gap-2">
          <FileText size={12} weight="fill" className="text-[#FF3E00]" /> docs.center
          <span className="text-zinc-400 normal-case tracking-normal ml-1" data-testid="docs-shots-count">
            · {shotsCount} figures on disk
          </span>
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={refreshShots}
            disabled={shotsBusy || busy}
            data-testid="docs-refresh-shots-btn"
            title="Re-capture every UI screenshot with headless Chromium (~40s). Follow up with REGENERATE ALL."
            className="font-mono text-[10px] tracking-[0.15em] uppercase border border-white text-white px-3 py-1.5 hover:bg-white hover:text-zinc-950 disabled:opacity-40 flex items-center gap-1.5"
          >
            <Camera size={11} weight="bold" />
            {shotsBusy ? "CAPTURING…" : "REFRESH SHOTS"}
          </button>
          <button
            onClick={regenerate}
            disabled={busy || shotsBusy}
            data-testid="docs-regenerate-btn"
            className="font-mono text-[10px] tracking-[0.15em] uppercase bg-[#FF3E00] text-white px-3 py-1.5 hover:bg-white hover:text-zinc-950 disabled:opacity-40 flex items-center gap-1.5"
          >
            <ArrowsClockwise size={11} weight="bold" />
            {busy ? "REGENERATING…" : "REGENERATE ALL"}
          </button>
        </div>
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        {items.map((it) => (
          <div
            key={it.kind}
            data-testid={`docs-card-${it.kind}`}
            className="border border-zinc-200 hover:border-zinc-950 transition-colors p-4 flex flex-col"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-sm font-bold text-zinc-950">{it.label}</div>
                <div className="text-[10px] font-mono tracking-wider text-zinc-500 uppercase mt-0.5">
                  {it.owner_only ? "OWNER ONLY" : "PUBLISHED · ALL USERS"}
                </div>
              </div>
              {it.owner_only && <Lock size={12} className="text-[#D0021B]" />}
            </div>
            {it.regenerated_at ? (
              <div className="text-[10px] font-mono text-zinc-500 mb-3">
                Last built {new Date(it.regenerated_at).toLocaleString()}<br/>
                by <span className="text-zinc-950">{it.regenerated_by}</span> · {(it.size_bytes / 1024).toFixed(0)} KB
              </div>
            ) : (
              <div className="text-[10px] font-mono text-amber-700 mb-3">NOT YET GENERATED — click REGENERATE ALL</div>
            )}
            <a
              href={dlUrl(it.kind)}
              data-testid={`docs-download-${it.kind}`}
              className={`mt-auto font-mono text-[10px] tracking-[0.15em] uppercase inline-flex items-center gap-1.5 border px-3 py-1.5 justify-center ${
                it.regenerated_at
                  ? "border-zinc-950 hover:bg-zinc-950 hover:text-white"
                  : "border-zinc-300 text-zinc-400 pointer-events-none"
              }`}
            >
              <DownloadSimple size={11} weight="bold" /> Download .docx
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}
